import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, CheckCircle2, Clock, XCircle, Briefcase, X, DollarSign, ExternalLink,
  Star, ShieldOff, Link as LinkIcon, FileImage, FileText,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const UNITS = [
  { v: "project", label: "Au projet" },
  { v: "day", label: "Par jour" },
  { v: "hour", label: "Par heure" },
  { v: "month", label: "Par mois" },
];

/**
 * FreelanceServices — Publier & gérer ses services (validation admin requise).
 */
export default function FreelanceServices() {
  const { user } = useAuth();
  const [entitlements, setEntitlements] = useState(null);
  const [services, setServices] = useState([]);
  const [externalMissions, setExternalMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [openExt, setOpenExt] = useState(false);
  const [openCert, setOpenCert] = useState(false);
  const [commissionsMine, setCommissionsMine] = useState({ items: [], totals: {} });

  const canPublish = user?.role === "freelance" || ["admin", "super-admin"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s, x, c] = await Promise.all([
        api.get("/v1/business/entitlements/me"),
        api.get("/v1/business/services/mine"),
        api.get("/v1/business/portfolio/external-missions/mine"),
        api.get("/v1/business/commissions/mine"),
      ]);
      setEntitlements(e.data.entitlements);
      setServices(s.data.items || []);
      setExternalMissions(x.data.items || []);
      setCommissionsMine(c.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canPublish) load(); }, [canPublish, load]);

  const del = async (id) => {
    if (!window.confirm("Supprimer ce service ?")) return;
    try {
      await api.delete(`/v1/business/services/${id}`);
      toast.success("Service supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delExt = async (id) => {
    if (!window.confirm("Supprimer cette mission externe ?")) return;
    try {
      await api.delete(`/v1/business/portfolio/external-missions/${id}`);
      toast.success("Mission supprimée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!canPublish) {
    return (
      <PrivateLayout title="Mes services">
        <div className="afr-empty" data-testid="services-forbidden">
          <div className="afr-empty__title">Réservé aux freelances</div>
          <div className="afr-empty__lead">Cette page permet aux freelances de publier leurs services.</div>
        </div>
      </PrivateLayout>
    );
  }

  const frq = entitlements?.freelance || {};
  const commissionRate = ((frq.commission_rate || 0.1) * 100).toFixed(0);

  return (
    <PrivateLayout title="💼 Mes services & missions">
      {loading && <div data-testid="services-loading">Chargement…</div>}

      {!loading && (
        <>
          {/* Quota banner */}
          <div className="afr-card" style={{ padding: 14, marginBottom: 16, borderLeft: "4px solid #009640", background: "rgba(0,150,64,0.04)" }} data-testid="services-quota">
            <strong>
              Services actifs : {frq.active_services || 0}{" "}
              {frq.free_services_limit > 0 ? `/ ${frq.free_services_limit}` : "· illimités"}
            </strong>
            <div className="afr-muted" style={{ fontSize: "0.86rem", marginTop: 4 }}>
              Publication gratuite. Chaque nouveau service passe en validation Super Admin avant d'apparaître publiquement.
              Les missions réalisées via AFRICTAL sont <strong>certifiées</strong> et génèrent une commission plateforme de <strong>{commissionRate}%</strong>.
              Les missions externes ne génèrent aucune commission.
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            <button className="afr-btn afr-btn--primary" onClick={() => setOpenForm(true)} data-testid="btn-new-service">
              <Plus size={14} /> Publier un service
            </button>
            <button className="afr-btn afr-btn--secondary" onClick={() => setOpenCert(true)} data-testid="btn-new-certified">
              <Star size={14} /> Enregistrer une mission AFRICTAL Certifiée
            </button>
            <button className="afr-btn afr-btn--outline" onClick={() => setOpenExt(true)} data-testid="btn-new-external">
              <ExternalLink size={14} /> Ajouter une mission externe
            </button>
          </div>

          {/* Services list */}
          <h2 style={{ fontSize: "1.1rem", fontFamily: "'Outfit', sans-serif", marginTop: 20, marginBottom: 10 }}>Mes services</h2>
          {services.length === 0 && (
            <div className="afr-empty" data-testid="services-empty">
              <div className="afr-empty__title">Aucun service publié</div>
              <div className="afr-empty__lead">Commencez par publier votre premier service.</div>
            </div>
          )}
          <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }} data-testid="services-list">
            {services.map((s) => (
              <article key={s.id} className="afr-card" data-testid={`service-${s.id}`} style={{ padding: 14, borderLeft: `4px solid ${statusColor(s.status)}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <strong>{s.title}</strong>
                  <StatusBadge status={s.status} />
                </div>
                <div className="afr-muted" style={{ fontSize: "0.82rem", marginTop: 4 }}>{s.category || "—"}</div>
                {s.description && <p style={{ fontSize: "0.86rem", marginTop: 8 }}>{s.description.slice(0, 200)}</p>}
                {s.price_from != null && (
                  <div style={{ marginTop: 8, fontSize: "0.9rem" }}>
                    <DollarSign size={12} style={{ verticalAlign: "middle" }} /> Dès <strong>{s.price_from} {s.currency}</strong> / {UNITS.find((u) => u.v === s.unit)?.label || s.unit}
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => del(s.id)} data-testid={`del-service-${s.id}`}>
                    <Trash2 size={12} /> Supprimer
                  </button>
                </div>
              </article>
            ))}
          </div>

          {/* External missions & commissions */}
          <h2 style={{ fontSize: "1.1rem", fontFamily: "'Outfit', sans-serif", marginTop: 24, marginBottom: 10 }}>
            Missions externes déclarées ({externalMissions.length})
          </h2>
          {externalMissions.length === 0 ? (
            <p className="afr-muted" style={{ fontSize: "0.9rem" }}>Aucune mission externe ajoutée à votre portfolio.</p>
          ) : (
            <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }} data-testid="external-list">
              {externalMissions.map((m) => (
                <article key={m.id} className="afr-card" data-testid={`external-${m.id}`} style={{ padding: 14, borderLeft: "4px solid #64748b" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <strong>{m.title}</strong>
                    <span className="afr-badge" style={{ background: "rgba(100,116,139,0.10)", color: "#64748b" }}>Externe · Non certifiée</span>
                  </div>
                  {m.client_name && <div className="afr-muted" style={{ fontSize: "0.82rem" }}>Client : {m.client_name}</div>}
                  {m.description && <p style={{ fontSize: "0.86rem", marginTop: 6 }}>{m.description.slice(0, 200)}</p>}
                  {m.gross_amount && <div style={{ marginTop: 6, fontSize: "0.86rem" }}>Montant : {m.gross_amount} {m.currency || "XOF"}</div>}
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => delExt(m.id)} data-testid={`del-external-${m.id}`}>
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Commissions récapitulatif */}
          <h2 style={{ fontSize: "1.1rem", fontFamily: "'Outfit', sans-serif", marginTop: 24, marginBottom: 10 }}>Mes commissions & revenus</h2>
          <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }} data-testid="commissions-totals">
            <SmallKpi label="Missions certifiées" value={commissionsMine.totals?.certified_count ?? 0} accent="#F59E0B" />
            <SmallKpi label="Missions externes" value={commissionsMine.totals?.external_count ?? 0} accent="#64748b" />
            <SmallKpi label="Volume brut" value={fmtMoney(commissionsMine.totals?.gross_amount)} accent="#0F2A42" />
            <SmallKpi label="Commissions AFRICTAL" value={fmtMoney(commissionsMine.totals?.commission_amount)} accent="#B45309" />
            <SmallKpi label="Vous touchez" value={fmtMoney(commissionsMine.totals?.freelance_amount)} accent="#15803D" />
          </div>
        </>
      )}

      {openForm && <ServiceForm onClose={() => { setOpenForm(false); load(); }} />}
      {openExt && <ExternalMissionForm onClose={() => { setOpenExt(false); load(); }} />}
      {openCert && <CertifiedMissionForm onClose={() => { setOpenCert(false); load(); }} />}
    </PrivateLayout>
  );
}

function ServiceForm({ onClose }) {
  const [form, setForm] = useState({ title: "", description: "", category: "", price_from: "", currency: "XOF", unit: "project" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.price_from === "") delete payload.price_from;
      else payload.price_from = parseFloat(payload.price_from);
      await api.post("/v1/business/services", payload);
      toast.success("Service publié · en attente de validation");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose} title="Publier un service" testid="service-form-modal">
      <form onSubmit={submit}>
        <Row><input required placeholder="Titre du service" value={form.title} onChange={set("title")} data-testid="svc-title" /></Row>
        <Row><textarea placeholder="Description détaillée" rows={4} value={form.description} onChange={set("description")} data-testid="svc-desc" /></Row>
        <Row><input placeholder="Catégorie (ex : Développement, Design)" value={form.category} onChange={set("category")} data-testid="svc-cat" /></Row>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <Row><input type="number" min="0" step="1" placeholder="Prix à partir de" value={form.price_from} onChange={set("price_from")} data-testid="svc-price" /></Row>
          <Row><input placeholder="Devise" value={form.currency} onChange={set("currency")} data-testid="svc-cur" /></Row>
          <Row>
            <select value={form.unit} onChange={set("unit")} data-testid="svc-unit">
              {UNITS.map((u) => <option key={u.v} value={u.v}>{u.label}</option>)}
            </select>
          </Row>
        </div>
        <button type="submit" className="afr-btn afr-btn--primary" disabled={saving} data-testid="svc-submit">
          <Save size={14} /> Publier
        </button>
      </form>
    </Modal>
  );
}

function ExternalMissionForm({ onClose }) {
  const [form, setForm] = useState({
    title: "", client_name: "", description: "",
    started_at: "", ended_at: "",
    gross_amount: "", currency: "XOF",
    proof_url: "", proof_type: "link",
  });
  const [saving, setSaving] = useState(false);
  const [confidential, setConfidential] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    // Client-side validation (miroir du backend)
    if ((form.title || "").trim().length < 3) return toast.error("Le titre doit contenir au moins 3 caractères");
    const client = confidential ? "Confidentiel" : (form.client_name || "").trim();
    if (!client) return toast.error("Le nom du client (ou 'Confidentiel') est obligatoire");
    if ((form.description || "").trim().length < 10) return toast.error("La description doit contenir au moins 10 caractères");
    if (!form.started_at || !form.ended_at) return toast.error("La période de réalisation est obligatoire");
    if (new Date(form.ended_at) < new Date(form.started_at)) return toast.error("La date de fin doit être postérieure à la date de début");

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        client_name: client,
        description: form.description.trim(),
        started_at: form.started_at,
        ended_at: form.ended_at,
        currency: form.currency,
      };
      if (form.gross_amount !== "") payload.gross_amount = parseFloat(form.gross_amount);
      if (form.proof_url && form.proof_url.trim()) {
        payload.proof_url = form.proof_url.trim();
        payload.proof_type = form.proof_type || "link";
      }
      await api.post("/v1/business/portfolio/external-missions", payload);
      toast.success("Mission externe ajoutée · Non certifiée AFRICTAL");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} title="Ajouter une mission externe" testid="external-form-modal">
      <div className="afr-card" style={{ padding: 12, marginBottom: 14, background: "rgba(100,116,139,0.06)", borderLeft: "3px solid #64748b" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <ShieldOff size={16} color="#64748b" />
          <strong>Mission Externe — Non certifiée AFRICTAL</strong>
        </div>
        <div className="afr-muted" style={{ fontSize: "0.82rem", marginTop: 4 }}>
          Mission réalisée hors plateforme AFRICTAL. Aucune commission ne s'applique. Elle apparaîtra dans votre portfolio marquée comme telle.
        </div>
      </div>
      <form onSubmit={submit}>
        <Row><input required minLength={3} placeholder="Titre de la mission *" value={form.title} onChange={set("title")} data-testid="ext-title" /></Row>
        <div className="afr-card" style={{ padding: "0.7rem", marginBottom: "0.6rem" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, fontSize: "0.85rem" }}>
            <input type="checkbox" checked={confidential} onChange={(e) => setConfidential(e.target.checked)} data-testid="ext-confidential" />
            Client confidentiel
          </label>
          <input placeholder="Nom du client *" value={confidential ? "Confidentiel" : form.client_name} onChange={set("client_name")} disabled={confidential} required={!confidential} data-testid="ext-client" />
        </div>
        <Row><textarea required minLength={10} placeholder="Description détaillée * (min. 10 caractères)" rows={4} value={form.description} onChange={set("description")} data-testid="ext-desc" /></Row>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Row><input required type="date" value={form.started_at} onChange={set("started_at")} data-testid="ext-start" /></Row>
          <Row><input required type="date" value={form.ended_at} onChange={set("ended_at")} data-testid="ext-end" /></Row>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Row><input type="number" min="0" placeholder="Montant brut (optionnel)" value={form.gross_amount} onChange={set("gross_amount")} data-testid="ext-amount" /></Row>
          <Row><input placeholder="Devise" value={form.currency} onChange={set("currency")} data-testid="ext-cur" /></Row>
        </div>
        <div className="afr-card" style={{ padding: "0.7rem", marginBottom: "0.6rem" }} data-testid="ext-proof-block">
          <span className="afr-muted" style={{ fontSize: "0.8rem" }}>Justificatif (optionnel — image, PDF ou lien)</span>
          <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            {[
              { v: "link", label: "Lien", Icon: LinkIcon },
              { v: "image", label: "Image", Icon: FileImage },
              { v: "pdf", label: "PDF", Icon: FileText },
            ].map((t) => (
              <button
                type="button"
                key={t.v}
                onClick={() => setForm((f) => ({ ...f, proof_type: t.v }))}
                className={`afr-btn afr-btn--sm ${form.proof_type === t.v ? "afr-btn--primary" : "afr-btn--outline"}`}
                data-testid={`ext-proof-${t.v}`}
              >
                <t.Icon size={12} /> {t.label}
              </button>
            ))}
          </div>
          <input placeholder="URL du justificatif" value={form.proof_url} onChange={set("proof_url")} data-testid="ext-proof-url" style={{ marginTop: 8, width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "'Outfit', sans-serif", fontSize: "0.95rem" }} />
        </div>
        <button type="submit" className="afr-btn afr-btn--primary" disabled={saving} data-testid="ext-submit">
          <Save size={14} /> Ajouter au portfolio
        </button>
      </form>
    </Modal>
  );
}

function CertifiedMissionForm({ onClose }) {
  const [form, setForm] = useState({
    mission_title: "", client_name: "",
    gross_amount: "", currency: "XOF", notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Preview automatique dès qu'il y a un montant
  useEffect(() => {
    const amount = parseFloat(form.gross_amount);
    if (!amount || amount <= 0) { setPreview(null); return; }
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/v1/business/commissions/preview", {
          mode: "certified",
          mission_title: form.mission_title || "Mission",
          gross_amount: amount,
          currency: form.currency,
        });
        setPreview(data);
      } catch (_e) { setPreview(null); }
      finally { setPreviewing(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [form.gross_amount, form.currency, form.mission_title]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.mission_title || form.mission_title.trim().length < 1) return toast.error("Titre obligatoire");
    const amount = parseFloat(form.gross_amount);
    if (!amount || amount <= 0) return toast.error("Le montant brut doit être supérieur à 0");
    if (!preview) return toast.error("Attendez le calcul du split de commission");
    setSaving(true);
    try {
      const { data } = await api.post("/v1/business/commissions", {
        mode: "certified",
        mission_title: form.mission_title.trim(),
        client_name: form.client_name.trim() || undefined,
        gross_amount: amount,
        currency: form.currency,
        notes: form.notes,
      });
      // Additif : auto-crédit wallet AFRICTAL (V3.5 Payment Core)
      try {
        const cid = data?.commission?.id;
        if (cid) await api.post(`/v1/payments/wallet/credit-from-commission/${cid}`);
      } catch (_e) { /* wallet credit best-effort */ }
      toast.success("Mission AFRICTAL Certifiée enregistrée ⭐ · Wallet crédité");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} title="Enregistrer une mission AFRICTAL Certifiée" testid="certified-form-modal">
      <div className="afr-card" style={{ padding: 12, marginBottom: 14, background: "rgba(245,158,11,0.06)", borderLeft: "3px solid #F59E0B" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Star size={16} color="#F59E0B" fill="#F59E0B" />
          <strong>Mission AFRICTAL Certifiée</strong>
        </div>
        <div className="afr-muted" style={{ fontSize: "0.82rem", marginTop: 4 }}>
          Mission réalisée via la plateforme AFRICTAL. Une commission plateforme s'applique automatiquement.
          Le montant est affiché avant validation.
        </div>
      </div>
      <form onSubmit={submit}>
        <Row><input required placeholder="Titre de la mission" value={form.mission_title} onChange={set("mission_title")} data-testid="cert-title" /></Row>
        <Row><input placeholder="Nom du client (optionnel)" value={form.client_name} onChange={set("client_name")} data-testid="cert-client" /></Row>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Row><input required type="number" min="1" step="1" placeholder="Montant brut *" value={form.gross_amount} onChange={set("gross_amount")} data-testid="cert-amount" /></Row>
          <Row><input placeholder="Devise" value={form.currency} onChange={set("currency")} data-testid="cert-cur" /></Row>
        </div>

        {/* Preview du split — affiché AVANT validation */}
        <div
          className="afr-card"
          data-testid="cert-preview"
          style={{
            padding: 14, marginBottom: 14,
            background: preview ? "rgba(21,128,61,0.06)" : "rgba(148,163,184,0.06)",
            borderLeft: `3px solid ${preview ? "#15803D" : "#94a3b8"}`,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <strong style={{ fontSize: "0.9rem" }}>💰 Répartition avant validation</strong>
            {previewing && <span className="afr-muted" style={{ fontSize: "0.78rem" }}>calcul…</span>}
          </div>
          {preview ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 6 }}>
              <div>
                <div className="afr-muted" style={{ fontSize: "0.72rem" }}>Mission (brut)</div>
                <div style={{ fontWeight: 700 }} data-testid="cert-preview-gross">{fmtMoney(preview.gross_amount, preview.currency)}</div>
              </div>
              <div>
                <div className="afr-muted" style={{ fontSize: "0.72rem" }}>Commission AFRICTAL</div>
                <div style={{ fontWeight: 700, color: "#B45309" }} data-testid="cert-preview-commission">
                  {(preview.commission_rate * 100).toFixed(0)}% · {fmtMoney(preview.commission_amount, preview.currency)}
                </div>
              </div>
              <div>
                <div className="afr-muted" style={{ fontSize: "0.72rem" }}>Vous recevrez</div>
                <div style={{ fontWeight: 700, color: "#15803D", fontSize: "1.05rem" }} data-testid="cert-preview-net">
                  {fmtMoney(preview.freelance_amount, preview.currency)}
                </div>
              </div>
            </div>
          ) : (
            <div className="afr-muted" style={{ fontSize: "0.82rem" }}>Saisissez un montant pour voir la commission AFRICTAL et le montant que vous recevrez.</div>
          )}
        </div>

        <Row><textarea placeholder="Notes (optionnel)" rows={2} value={form.notes} onChange={set("notes")} data-testid="cert-notes" /></Row>

        <button type="submit" className="afr-btn afr-btn--primary" disabled={saving || !preview} data-testid="cert-submit">
          <Star size={14} /> Valider la mission
        </button>
      </form>
    </Modal>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div role="dialog" data-testid={testid} onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,42,66,0.5)", display: "grid", placeItems: "center", zIndex: 90, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, maxWidth: 560, width: "100%", padding: 20, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <strong style={{ fontSize: "1.05rem" }}>{title}</strong>
          <button type="button" onClick={onClose} aria-label="Fermer" style={{ border: 0, background: "transparent", cursor: "pointer" }}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Row({ children }) {
  return (
    <div className="afr-card" style={{ padding: "0.7rem", marginBottom: "0.6rem" }}>
      <style>{`
        .afr-card input, .afr-card textarea, .afr-card select {
          width: 100%; border: none; outline: none; background: transparent;
          font-family: 'Outfit', sans-serif; font-size: 0.95rem; color: var(--afr-secondary);
        }
      `}</style>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = {
    pending: { label: "En attente", color: "#B45309", Icon: Clock, bg: "rgba(180,83,9,0.12)" },
    validated: { label: "Validé", color: "#15803D", Icon: CheckCircle2, bg: "rgba(21,128,61,0.12)" },
    rejected: { label: "Rejeté", color: "#B91C1C", Icon: XCircle, bg: "rgba(185,28,28,0.10)" },
  }[status] || { label: status, color: "#64748b", Icon: Briefcase, bg: "rgba(100,116,139,0.10)" };
  const I = meta.Icon;
  return (
    <span className="afr-badge" style={{ background: meta.bg, color: meta.color }}>
      <I size={11} /> {meta.label}
    </span>
  );
}

function SmallKpi({ label, value, accent }) {
  return (
    <div className="afr-card" style={{ padding: 12, borderLeft: `3px solid ${accent}` }}>
      <div className="afr-muted" style={{ fontSize: "0.78rem" }}>{label}</div>
      <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--afr-secondary)", marginTop: 2 }}>{value}</div>
    </div>
  );
}

function statusColor(s) { return s === "validated" ? "#15803D" : s === "rejected" ? "#B91C1C" : "#B45309"; }
function fmtMoney(n, cur = "XOF") {
  if (n == null) return "—";
  try { return `${new Intl.NumberFormat("fr-FR").format(n)} ${cur}`; } catch { return `${n} ${cur}`; }
}
